import { getConfig } from "../constants/config";
import { ethers } from "ethers-v5";
import { getTotalAmountIn } from "./getTotalAmountIn";
import { getTokenIn } from "./getTokenIn";
import { getTokenOut } from "./getTokenOut";
import { getSwapTypes } from "./getSwapTypes";
import tokenService from "../../common/tokenService";

/**
 * Extracts and calculates detailed information about a swap route.
 * Processes single or multi-path routes and provides details about the swap.
 *
 * @param {Array} route - The route array from getRoute response
 *
 * @returns {Promise<Object>} Route details containing:
 *   - totalOut {ethers.BigNumber} - Total output amount in raw units
 *   - totalOutFormatted {string} - Total output amount formatted with token decimals
 *   - pathPercentages {number[]} - Percentage split between paths (e.g., [60, 40] for 60-40 split)
 *   - pathCount {number} - Number of paths in the route
 *   - amountIn {ethers.BigNumber} - Total input amount in raw units
 *   - amountInFormatted {string} - Total input amount formatted with token decimals
 *   - swapTypes {number[]} - Array of swap types for each step
 *   - tokenIn {string} - Input token address
 *   - tokenOut {string} - Output token address
 *   - tokenInData {Object} - Input token metadata from Symphony
 *   - tokenOutData {Object} - Output token metadata from Symphony
 *   - route {Array} - Original route array
 *
 * @throws {Error} If token data is missing from Symphony
 * @throws {Error} If path structure is invalid
 *
 * @example
 * // Example 1: Get details for a single path route
 * const route = await getRoute({ ... }); // Single path route
 * const details = await getRouteDetails(route);
 * console.log(details.totalOutFormatted); // "1.5" USDC
 * console.log(details.pathCount); // 1
 *
 * @example
 * // Example 2: Get details for a multi-path route
 * const route = await getRoute({ ... }); // Multi-path route
 * const details = await getRouteDetails(route);
 * console.log(details.pathPercentages); // [80, 20] for 80-20 split
 * console.log(details.pathCount); // 2
 *
 * @example
 * // Example 3: Access token information
 * const details = await getRouteDetails(route);
 * console.log(details.tokenInData.attributes.decimals); // 18 for ETH
 * console.log(details.tokenOutData.attributes.symbol); // "USDC"
 */
export const getRouteDetails = async (route) => {
  try {
    // Get config for non-token data
    const config = getConfig();
    const { chainId, additionalTokens, overrideDefaultTokens, nativeAddress } =
      config;

    // Get updated token list with user's configuration applied
    const tokens = await tokenService.getAsyncTokenList(
      chainId,
      additionalTokens,
      overrideDefaultTokens,
      nativeAddress
    );

    // Validate required parameters
    if (!route || !Array.isArray(route) || route?.length === 0) {
      console.error("Invalid or missing `route`: Must be an array.");
      return {
        totalOut: undefined,
        pathPercentages: undefined,
        pathCount: undefined,
        totalOutFormatted: undefined,
        amountIn: undefined,
        amountInFormatted: undefined,
        swapTypes: undefined,
        tokenIn: undefined,
        tokenOut: undefined,
        tokenInData: undefined,
        tokenOutData: undefined,
        route: route,
      };
    }
    const totalAmountIn = await getTotalAmountIn(route);

    const amountIn = totalAmountIn.amountIn;
    const amountInFormatted = totalAmountIn.amountInFormatted;
    const tokenIn = getTokenIn(route);
    const tokenOut = getTokenOut(route);
    const swapTypes = getSwapTypes(route);
    // Retrieve token decimals
    const tokenInData = tokens[tokenIn];
    const tokenOutData = tokens[tokenOut];

    if (!tokenInData || !tokenInData.attributes?.decimals) {
      throw new Error(
        `Token ${tokenIn} data or decimals missing. This might mean token is not listed on Symphony.`
      );
    }
    if (!tokenOutData || !tokenOutData.attributes?.decimals) {
      throw new Error(
        `Token ${tokenOut} data or decimals missing. This might mean token is not listed on Symphony.`
      );
    }

    const tokenInDecimals = tokenInData.attributes.decimals;
    const tokenOutDecimals = tokenOutData.attributes.decimals;

    // Initialize outputs
    let totalOut = ethers.BigNumber.from(0);

    const pathPercentages = [];
    const pathCount = route.length;

    for (let i = 0; i < pathCount; i++) {
      const path = route[i];

      // Validate path structure
      if (!Array.isArray(path) || path.length === 0) {
        throw new Error(
          `Invalid path at index ${i}: Must be a non-empty array.`
        );
      }

      const pathOut = ethers.BigNumber.from(
        path[path.length - 1]?.amountOutMin || 0
      );
      const pathIn = ethers.BigNumber.from(path[0]?.amountIn || 0);

      totalOut = totalOut.add(pathOut);

      // Calculate path percentage
      const pathInFormatted = Number(
        ethers.utils.formatUnits(pathIn, tokenInDecimals)
      );

      const pathPercent = Math.round(
        (pathInFormatted / Number(amountInFormatted)) * 100
      );

      pathPercentages.push(pathPercent);
    }

    // Format total output
    const totalOutFormatted = ethers.utils.formatUnits(
      totalOut,
      tokenOutDecimals
    );
    return {
      totalOut,
      pathPercentages,
      pathCount,
      totalOutFormatted,
      amountIn,
      amountInFormatted,
      swapTypes,
      tokenIn,
      tokenOut,
      tokenInData,
      tokenOutData,
      route,
    };
  } catch (error) {
    console.error("Error in getRouteDetails:", error);
    throw error;
  }
};
